import type { ReportResult } from '@creanizer/types'
import { FileText, Shield, AlertTriangle, ChevronRight } from 'lucide-react'

interface ReportViewerProps {
  document: ReportResult
}

export function ReportViewer({ document }: ReportViewerProps) {
  // データが正しい形式でない場合のフォールバック
  if (!document || !document.header) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">報告書</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  // 機密区分のスタイル取得
  const getClassificationStyle = (classification?: string) => {
    if (!classification) return ''
    const c = classification.toLowerCase()
    if (c.includes('極秘') || c.includes('top secret')) {
      return 'bg-red-600 text-white'
    }
    if (c.includes('秘密') || c.includes('secret')) {
      return 'bg-orange-600 text-white'
    }
    if (c.includes('機密') || c.includes('confidential')) {
      return 'bg-yellow-600 text-white'
    }
    return 'bg-gray-600 text-white'
  }
  
  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg">
      {/* ヘッダー（公式文書風） */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              {document.header?.organization || '組織名不明'}
            </h1>
            {document.header?.department && (
              <p className="text-gray-300">{document.header.department}</p>
            )}
          </div>
          
          <div className="text-right">
            <p className="text-sm text-gray-300">文書番号</p>
            <p className="font-mono font-bold">{document.header?.documentNumber || 'N/A'}</p>
            <p className="text-sm text-gray-300 mt-2">{document.header?.date || new Date().toLocaleDateString()}</p>
          </div>
        </div>
        
        {/* 機密区分 */}
        {document.header?.classification && (
          <div className="mt-4">
            <span className={`inline-block px-3 py-1 rounded font-bold text-sm ${getClassificationStyle(document.header.classification)}`}>
              {document.header.classification}
            </span>
          </div>
        )}
      </div>
      
      {/* 報告書本体 */}
      <div className="p-8">
        {/* タイトル */}
        <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
          {document.title || '無題の報告書'}
        </h2>
        
        {/* 著者情報 */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">作成者</p>
              <p className="font-medium text-gray-900">{document.author?.name || '作成者不明'}</p>
              {document.author?.title && (
                <p className="text-sm text-gray-600">{document.author.title}</p>
              )}
            </div>
            {document.author?.signature && (
              <div className="text-right">
                <p className="text-sm text-gray-600">署名</p>
                <p className="font-signature text-2xl text-gray-700">{document.author.signature}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* 要約 */}
        {document.abstract && (
          <div className="mb-8 p-4 bg-blue-50 border-l-4 border-blue-500">
            <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              要約
            </h3>
            <p className="text-gray-700 leading-relaxed">{document.abstract}</p>
          </div>
        )}
        
        {/* セクション */}
        <div className="space-y-8">
          {(document.sections || []).map((section, sectionIndex) => (
            <div key={sectionIndex}>
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="text-blue-600">{sectionIndex + 1}.</span>
                {section.heading}
              </h3>
              
              <div className="ml-6">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {section.content}
                </p>
                
                {/* サブセクション */}
                {section.subsections && section.subsections.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {section.subsections.map((subsection, subIndex) => (
                      <div key={subIndex} className="ml-4">
                        <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                          {sectionIndex + 1}.{subIndex + 1} {subsection.heading}
                        </h4>
                        <p className="text-gray-700 ml-6 leading-relaxed">
                          {subsection.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* 結論 */}
        <div className="mt-8 p-4 bg-gray-100 rounded-lg">
          <h3 className="font-bold text-gray-900 mb-2">結論</h3>
          <p className="text-gray-700 leading-relaxed">{document.conclusion || '結論なし'}</p>
        </div>
        
        {/* 推奨事項 */}
        {document.recommendations && document.recommendations.length > 0 && (
          <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-500">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              推奨事項
            </h3>
            <ul className="space-y-2">
              {document.recommendations.map((rec, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">•</span>
                  <span className="text-gray-700">{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* 付録 */}
        {document.appendices && document.appendices.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="font-bold text-gray-900 mb-4">付録</h3>
            {document.appendices.map((appendix, index) => (
              <div key={index} className="mb-4">
                <h4 className="font-medium text-gray-800 mb-2">
                  付録 {String.fromCharCode(65 + index)}. {appendix.title}
                </h4>
                <div className="ml-4 text-gray-700 text-sm">
                  {appendix.content}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* 参照 */}
        {document.references && document.references.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h3 className="font-bold text-gray-900 mb-2">参照文献</h3>
            <ol className="list-decimal list-inside space-y-1">
              {document.references.map((ref, index) => (
                <li key={index} className="text-sm text-gray-600">
                  {ref.citation}
                </li>
              ))}
            </ol>
          </div>
        )}
        
        {/* メタデータ */}
        {document.metadata && (
          <div className="mt-8 pt-4 border-t border-gray-200 text-xs text-gray-500">
            <div className="flex justify-between">
              <span>生成日時: {document.metadata.generatedAt}</span>
              <span>文字数: {document.metadata.wordCount}</span>
              {document.metadata.pageCount && (
                <span>ページ数: {document.metadata.pageCount}</span>
              )}
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        .font-signature {
          font-family: 'Brush Script MT', cursive;
        }
      `}</style>
    </div>
  )
}